import { GetUserDto } from '@modeso/aumico-lib-users-fe';
import { IPersonDTO } from '@modeso/types__aumico-lib-companies-be';
import { PersonProjectDto, PersonProjectTableValue } from '../../../../../../aumico-lib-projects-fe/src/lib';

/** Year of `periodEnd` — "Steuerperiode <Jahr>". */
export function taxYearOf(project: Pick<PersonProjectDto, 'periodEnd'>): number {
    return new Date(project.periodEnd).getUTCFullYear();
}

/**
 * REV-22155 — person projects → table rows, for the projects page and the person's "Projekte" tab:
 * user names for mandate manager / clerk, tag names of the person.
 */
export function toPersonProjectTableData(
    projects: PersonProjectDto[],
    users: GetUserDto[],
    persons: IPersonDTO[],
    tagNamesById: Record<string, string>,
    unassignedLabel: string,
): PersonProjectTableValue[] {
    const usersById = new Map(users.map((u) => [u.id, u]));
    const personsById = new Map(persons.map((p) => [p._id, p]));
    const nameOf = (user: GetUserDto | undefined): string =>
        user ? `${user.firstName} ${user.lastName}` : unassignedLabel;

    return projects.map((project) => {
        const assignee = project.assignee ? usersById.get(project.assignee) : undefined;
        const clerk = project.clerk ? usersById.get(project.clerk) : undefined;
        return {
            projectId: project.projectId,
            personId: project.personId,
            personName: project.personName,
            taxYear: taxYearOf(project),
            projectStatus: project.projectStatus,
            tags: (personsById.get(project.personId)?.tags ?? [])
                .map((id) => tagNamesById[id])
                .filter((name): name is string => !!name),
            assignee: nameOf(assignee),
            assigneeDeleted: assignee?.isDeleted ?? false,
            clerk: nameOf(clerk),
            clerkDeleted: clerk?.isDeleted ?? false,
            workflowData: project.workflowData ?? null,
        };
    });
}

/** Year of each person's latest project — preselects the next year in "+ Neues Projekt". */
export function lastProjectYearByPersonId(projects: PersonProjectDto[]): Record<string, number> {
    const result: Record<string, number> = {};
    for (const project of projects) {
        result[project.personId] = Math.max(result[project.personId] ?? 0, taxYearOf(project));
    }
    return result;
}
